import { Injectable } from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {User} from '../model/user';
import {environment} from '../../environments/environment';
import {AppConfig} from '../shared/app-config';
import {catchError, map, publishReplay, refCount} from 'rxjs/operators';
import {ErrorDataService} from './error-data.service';
import {factory} from '../shared/app-logging';
import {MenuData, Context, User as UserDetails } from 'ngx-global-nav';

const logger = factory.getLogger('UserService');

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private user: Observable<User>;
  private fetchUserLastCallError = false;

  private menu: Observable<MenuData>;
  private fetchUserMenuLastCallError = false;

  private organizations: Observable<Context[]>;
  private fetchUserOrganizationsLastCallError = false;

  private userDetails: BehaviorSubject<User> = new BehaviorSubject<User>(null);

  constructor(private httpClient: HttpClient) {}

  getUserDetails(): Observable<User> {
    if (this.user && !this.fetchUserLastCallError) {
      logger.debug('getUserDetails: Cache hit.');
    }

    this.user = (this.user && !this.fetchUserLastCallError) ? this.user : this.fetchUserDetails();

    return this.user;
  }

  public fetchUserDetails(): Observable<User> {
    return this.httpClient.get(environment.contentLibraryApiBaseUrl + AppConfig.urlUserDetails).pipe(map(
      response => {
        const dataDetails: UserDetails = response['data']['details'];

        const user = new User(
          dataDetails,
          dataDetails.user_id,
          dataDetails.username,
          dataDetails.user_type,
          dataDetails.user_class
        );

        logger.debug(`${user.username} user fetched from REST`);
        this.fetchUserLastCallError = false;
        return user;
      }
      ), catchError((err: Error | HttpErrorResponse) => {
        this.fetchUserLastCallError = true;
        const message = `In UserService.fetchUser().`;
        return throwError(ErrorDataService.overLoadError(err, message));
      }),
      publishReplay(1),
      refCount()
    );
  }

  getUserMenu(user: User): Observable<MenuData> {
    if (this.menu && !this.fetchUserMenuLastCallError) {
      logger.debug('getUserMenu: Cache hit.');
    }

    this.menu = (this.menu && !this.fetchUserMenuLastCallError) ? this.menu : this.fetchUserMenu(user);

    return this.menu;
  }

  public fetchUserMenu(user: User): Observable<MenuData> {
    let urlService = AppConfig.urlUserMenu;
    urlService = urlService.replace('{user_id}', user.userId);

    return this.httpClient.get(environment.contentLibraryApiBaseUrl + urlService).pipe(map(
      response => {
        const data = response['data'];

        logger.debug(`${JSON.stringify(data)} user menu fetched from REST`);
        this.fetchUserMenuLastCallError = false;
        return data;
      }
      ), catchError((err: Error | HttpErrorResponse) => {
        this.fetchUserMenuLastCallError = true;
        const message = `In UserService.fetchUserMenu().`;
        return throwError(ErrorDataService.overLoadError(err, message));
      }),
      publishReplay(1),
      refCount()
    );
  }

  getUserOrganizations(user: User): Observable<Context[]> {
    if (this.organizations && !this.fetchUserOrganizationsLastCallError) {
      logger.debug('getUserOrganizations: Cache hit.');
    }

    this.organizations = (this.organizations && !this.fetchUserOrganizationsLastCallError) ? this.organizations :
      this.fetchUserOrganizations(user);

    return this.organizations;
  }

  public fetchUserOrganizations(user: User): Observable<Context[]> {
    let urlService = AppConfig.urlUserOrganizations;
    urlService = urlService.replace('{user_id}', user.userId);

    return this.httpClient.get(environment.contentLibraryApiBaseUrl + urlService).pipe(map(
      response => {
        const data = response['data'];

        logger.debug(`${JSON.stringify(data)} user organizations fetched from REST`);
        this.fetchUserOrganizationsLastCallError = false;
        return data;
      }
      ), catchError((err: Error | HttpErrorResponse) => {
        this.fetchUserOrganizationsLastCallError = true;
        const message = `In UserService.fetchUserOrganizations().`;
        return throwError(ErrorDataService.overLoadError(err, message));
      }),
      publishReplay(1),
      refCount()
    );
  }

  public setUserDetail(value: User): void {
    this.userDetails.next(value);
  }

  public getUserDetailData(): BehaviorSubject<User> {
    return this.userDetails;
  }

}
